import React, { useState, useEffect, useRef } from 'react';
import { Button, PopupMenu } from 'quantux-ui';
import { StyleSheet, css } from 'aphrodite/no-important';
import { titleCase, calculateAge, calculateTimeDiff, padZero } from '../../utils';
import { blue, salmon } from '../../theme';

const CaseCard = (props) => {
  const [showMenu, setShowMenu] = useState(false);
  const [patientString, setPatientString] = useState('N/A');
  const [isMarkedForDeletion, setIsMarkedForDeletion] = useState(false);

  const menuRef = useRef();

  const styles = StyleSheet.create({
    patientCard: {
      position: 'relative',
      width: '100%',
      minWidth: '318px',
      height: '120px',
      background: blue[700],
      marginLeft: '0',
      borderRadius: '4px'
    },
    patientCardField: {
      color: blue[200],
      fontWeight: '700',
      fontSize: '12px'
    },
    patientCardFieldValue: {
      color: 'white',
      fontSize: '12px',
      flexGrow: '1',
      display: 'flex',
      alignItems: 'center'
    },
    gridEllipsisMenuContainer: {
      minWidth: '210px',
      background: blue[800]
    },
    gridEllipsisMenuItem: {
      display: 'flex',
      alignItems: 'center',
      height: '40px',
      width: '100%',
      marginTop: '1px',
      paddingLeft: '8px',
      color: '#00A0FF',
      fontSize: '14px',
      background: '#002448',
      fill: '#00A0FF',
      ':not(.disabled):hover': {
        background: '#0066CC'
      }
    },
    gridEllipsisMenuItemDisabled: {
      color: '#00A0FF80',
      fill: '#00A0FF80'
    }
  });

  useEffect(() => {
    // if (Object.hasOwn(props.caseData, 'isMarkedForDeletion')) {
    //   setIsMarkedForDeletion(props.caseData.isMarkedForDeletion); // eslint-disable-line
    // }
    if (props.caseData.hasOwnProperty('isMarkedForDeletion')) { // eslint-disable-line
      setIsMarkedForDeletion(props.caseData.isMarkedForDeletion);
    }
  }, [props.caseData]);

  useEffect(() => {
    const {
      firstName = '',
      lastName = '',
      gender = '',
      dob = ''
    } = props.patientData;

    if (lastName.length > 0 && firstName.length > 0) {
      let pAge = '';
      let ptGender = '';
      if (dob.length > 0) {
        const tmpDob = dob.split('T');
        const tmp = tmpDob[0].split('-');
        pAge = calculateAge(`${tmp[1]}/${tmp[2]}/${tmp[0]}`);
      }

      ptGender = (gender.length > 0 && gender.toLowerCase() === 'male') ? 'M' : 'F';
      console.log(`${titleCase(lastName)}, ${titleCase(firstName)} (${pAge} ${ptGender})`);
      setPatientString(`${titleCase(lastName)}, ${titleCase(firstName)} (${pAge} ${ptGender})`);
    }
  }, [props.patientData]);

  const {
    anesthesiologists = [],
    anesthesiaPractices = [],
    surgeons = [],
    surgeonPractices = [],
    otherSurgicalProcedures = [],
    surgicalProcedures = [],
    scheduleStartHour,
    scheduleStartMinute,
    scheduleEndHour,
    scheduleEndMinute
  } = props.caseData;

  // Surgeon String
  let surgeonString = '';
  if (surgeons.length > 0) {
    surgeons.forEach((surg, idx) => {
      surgeonString += `${surg.fullName}${idx < surgeons.length - 1 ? ' | ' : ''}`;
    });
  }

  if (surgeonPractices && Array.isArray(surgeonPractices) && surgeonPractices.length > 0) {
    surgeonPractices.forEach((sp) => {
      if (sp.addedProviders.length > 0) {
        sp.addProviders.forEach((prov, idx) => {
          const tmp = titleCase(prov.fullName) ||
            titleCase(prov.displayName) ||
            `${titleCase(prov.providerLastName)}, ${titleCase(prov.providerFirstName)}`;
          surgeonString += `${tmp}${idx < sp.addProviders.length - 1 ? ' | ' : ''}`;
        });
      }
    });
  }

  // Anesthesia String
  let anesthesiaString = '';
  if (anesthesiologists && anesthesiologists.length > 0) {
    anesthesiologists.forEach((surg, idx) => {
      anesthesiaString += `${surg.fullName}${idx < anesthesiologists.length - 1 ? ' | ' : ''}`;
    });
  }

  if (anesthesiaPractices && Array.isArray(anesthesiaPractices) && anesthesiaPractices.length > 0) {
    anesthesiaPractices.forEach((ap) => {
      if (ap.addedProviders.length > 0) {
        ap.addProviders.forEach((prov, idx) => {
          const tmp = titleCase(prov.fullName) ||
            titleCase(prov.displayName) ||
            `${titleCase(prov.providerLastName)}, ${titleCase(prov.providerFirstName)}`;
          anesthesiaString += `${tmp}${idx < ap.addProviders.length - 1 ? ' | ' : ''}`;
        });
      }
    });
  }

  // Procedures String
  let procString = '';
  if (surgicalProcedures && surgicalProcedures.length > 0) {
    surgicalProcedures.forEach((proc, idx) => {
      procString += `${titleCase(proc.description)}${idx < surgicalProcedures.length - 1 ? ' | ' : ''}`;
    });
  }

  if (otherSurgicalProcedures && otherSurgicalProcedures.length > 0) {
    if (procString.length > 0) {
      procString = `${procString} | `;
    }
    otherSurgicalProcedures.forEach((proc, idx) => {
      procString += `${titleCase(proc.description)}${idx < otherSurgicalProcedures.length - 1 ? ' | ' : ''}`;
    });
  }

  // Block Duration
  let blockDuration = '?';
  if (scheduleStartHour !== null && scheduleStartMinute !== null && scheduleEndHour !== null && scheduleEndMinute !== null) {
    blockDuration = calculateTimeDiff(`${scheduleStartHour}:${scheduleStartMinute}`, `${scheduleEndHour}:${scheduleEndMinute}`);
  }

  const handleOnRevert = () => {
    // setIsMarkedForDeletion(false);
    props.onCancelDelete(props.caseData._id);
  };

  return (
    <div className={`p-1 ${css(styles.patientCard)}`}>
      {isMarkedForDeletion &&
        <div
          style={{
            position: 'absolute',
            minWidth: '318px',
            width: 'calc(100% - 7px)',
            height: '112px',
            background: 'black',
            opacity: '0.8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div>
            <div style={{ color: salmon[500], background: 'black' }}>
              Marked For Deletion
            </div>
            <Button
              btnType='primary'
              isOutlineBtn
              style={{
                width: '150px',
                height: '28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: '700'
              }}
              onClick={handleOnRevert}
            >
              Revert
            </Button>
          </div>
        </div>
      }
      {/* Top Row */}
      <div
        className='fx'
        style={{ justifyContent: 'space-between' }}
      >
        {/* Patients */}
        <div className='fx'>
          <div
            className={`ml-1 fx fx__v-center ${css(styles.patientCardField)}`}
            style={{ width: '45px' }}
          >
            Patient
          </div>
          <div className={`ml-1 text-truncate ${css(styles.patientCardFieldValue)}`}>
            {patientString}
          </div>
        </div>
        {/* Icons */}
        <div className='fx fx__v-center'>
          {/* Concurrency */}
          {props.isConcurrent &&
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="14"
              fill="none"
            >
              <path fill="#FF0" d="M11 12.74c1.657 0 3-2.57 3-5.74 0-3.17-1.343-5.74-3-5.74S8 3.83 8 7c0 3.17 1.343 5.74 3 5.74Z" />
              <path fill="#FF0" d="M8.04 11.87c-.33.08-.68.13-1.04.13-2.76 0-5-2.24-5-5s2.24-5 5-5c.36 0 .71.05 1.04.13.39-.56.88-1.12 1.49-1.63C8.75.19 7.9 0 7 0 3.14 0 0 3.14 0 7s3.14 7 7 7c.9 0 1.75-.19 2.53-.5-.61-.51-1.1-1.07-1.49-1.63ZM15 0c-.9 0-1.75.19-2.53.5.61.51 1.1 1.07 1.49 1.63.33-.08.68-.13 1.04-.13 2.76 0 5 2.24 5 5s-2.24 5-5 5c-.36 0-.71-.05-1.04-.13-.39.56-.88 1.12-1.49 1.63.78.31 1.63.5 2.53.5 3.86 0 7-3.14 7-7s-3.14-7-7-7Z" />
            </svg>
          }
          {/* Ellipsis */}
          <div
            className='ml-2'
            ref={menuRef}
            style={{
              height: '20px',
              position: 'relative',
              display: isMarkedForDeletion ? 'none' : ''
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              fill="none"
              onClick={() => setShowMenu(true)}
            >
              <path fill="#96C8FF" fillRule="evenodd" d="M2 0a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2Zm2.167 8.333a1.667 1.667 0 1 0 0 3.334 1.667 1.667 0 0 0 0-3.334Zm10 1.667a1.667 1.667 0 1 1 3.333 0 1.667 1.667 0 0 1-3.333 0ZM10 8.333a1.667 1.667 0 1 0 0 3.334 1.667 1.667 0 0 0 0-3.334Z" clipRule="evenodd" />
            </svg>
            <PopupMenu
              top={22}
              right={1}
              position='absolute'
              zIndex='2501'
              visible={showMenu}
              parent={menuRef}
              onOutsideClick={() => {
                setShowMenu(false);
              }}
            >
              <div className={css(styles.gridEllipsisMenuContainer)}>
                {/* Delete Case */}
                <div
                  className={css(styles.gridEllipsisMenuItem)}
                  onClick={() => {
                    setShowMenu(false);
                    // props.onDeleteCase(props.caseData._id);
                    props.onMarkForDeletion(props.caseData._id, props.index);
                  }}
                >
                  Delete Case
                </div>
                {/* Edit Case */}
                <div
                  className={css(styles.gridEllipsisMenuItem)}
                  onClick={() => {
                    props.onEditCase(props.caseData);
                    // props.onEditCase(props.index);
                  }}
                >
                  Edit Case
                </div>
              </div>
            </PopupMenu>
          </div>
          {/* Collapse */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            fill="none"
            className='ml-2'
            onClick={() => {
              props.onEditCase(props.caseData);
              // props.onEditCase(props.index);
            }}
          >
            <path fill="#96C8FF" fillRule="evenodd" d="M2 0a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2Zm8.92 9.464h3.587a.491.491 0 1 0 0-.983h-2.359l2.594-2.59a.49.49 0 1 0-.695-.693l-2.634 2.63V5.491a.491.491 0 0 0-.983 0v3.483a.49.49 0 0 0 .49.49Zm-5.43 2.05h2.36l-2.593 2.59a.49.49 0 1 0 .695.694l2.635-2.63v2.337a.491.491 0 0 0 .983 0v-3.483a.491.491 0 0 0-.491-.49H5.492a.491.491 0 0 0-.001.982Z" clipRule="evenodd" />
          </svg>
        </div>
      </div>
      {/* Surgeon */}
      <div className='fx' style={{ height: '24px' }}>
        <div className='fx fx__hv-center' style={{ width: '22px', height: '22px' }}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="23"
            height="20"
            fill="none"
          >
            <g
              fill="#00A0FF"
              clipPath="url(#a)"
            >
              <path d="M3.71 15.32c.04.63.29 1.36.81 1.92.54-.57.79-1.31.83-1.94.05-.81-.61-1.23-.8-1.33-.07.03-.9.44-.84 1.35ZM2.81 13.93c.45-1.18-.04-2.07-.37-2.59C.99 12.14 0 13.66 0 15.43v.19c1.23.2 2.35-.46 2.81-1.68v-.01Z" />
              <path d="m8.41 14.53-2.57-3.78H4.68v2.95c.21.11 1.04.61.98 1.62-.05.7-.32 1.51-.92 2.14.22.19.48.35.79.48v-.17c0-.15.12-.27.27-.27h.54c.34 0 .61.28.61.61 0 .33-.27.61-.61.61H5.8c-.15 0-.27-.12-.27-.27v-.2h-.04c-.38-.14-.7-.35-.97-.58-.26.23-.58.42-.95.56h-.03v.22c0 .15-.12.27-.27.27h-.54c-.34 0-.61-.28-.61-.61 0-.33.27-.61.61-.61h.54c.15 0 .27.12.27.27v.14c.29-.12.54-.27.76-.45-.58-.63-.85-1.43-.9-2.12-.06-1 .74-1.5.97-1.62v-2.94c-.59.04-1.15.18-1.66.42.35.56.88 1.56.39 2.85-.46 1.19-1.51 1.92-2.69 1.92-.14 0-.27-.02-.41-.04v3.74c0 .16.13.29.29.29H8.4v-5.43h.01Z" />
              <path d="M2.44 18.11c0 .17.14.31.31.31h.5v-.61h-.5c-.17 0-.31.14-.31.31v-.01ZM10.69 10.36s-.95.72-2.2.72c-.95 0-2.03-.71-2.03-.71v.73l2.12 3.16 2.13-3.17-.02-.74v.01ZM14.32 13.99c.57 1.36 2 1.84 2.82 1.68v-.24c0-1.76-.99-3.28-2.43-4.08-.34.53-.87 1.48-.39 2.64Z" />
              <path d="M16.78 16.01c-1.01 0-2.23-.68-2.74-1.91-.54-1.3.04-2.34.4-2.9-.6-.28-1.27-.45-1.98-.45H11.3l-2.58 3.73v5.48h8.13c.16 0 .29-.13.29-.29v-3.69c-.12.02-.23.04-.36.04v-.01Zm-4.39-1.57v-3.66h.4v3.66c.62.1 1.11.62 1.11 1.27 0 .72-.59 1.31-1.31 1.31-.72 0-1.31-.59-1.31-1.31 0-.65.49-1.17 1.11-1.27Zm2.69 3.82h-4.96c-.08 0-.15-.07-.15-.16s.07-.15.15-.15h4.96c.08 0 .15.07.15.16s-.07.15-.15.15Z" />
              <path d="M12.59 16.71c.55 0 1-.45 1-1s-.45-1-1-1-1 .45-1 1 .45 1 1 1ZM6.63 18.11c0-.17-.14-.31-.31-.31h-.5v.61h.5c.17 0 .31-.14.31-.31v.01ZM12.28 4.51l-.04-2.93C12.24.7 10.49 0 8.44 0 6.41.05 4.89.74 4.89 1.62l-.07 3.22c-.04.3-.04.63-.04.94 0 2.89 1.67 5 3.79 5s3.78-2.11 3.77-5.04c0-.43.01-.83-.06-1.23ZM5.81 7.99s-.17-.48-.25-.78c-.05-.19-.16-.93-.16-.93l.42.23L5.8 8l.01-.01Zm4.13 1.28H7.21c-.09 0-.17-.08-.17-.17 0-.09.08-.17.17-.17h2.73c.09 0 .17.08.17.17 0 .09-.08.17-.17.17Zm-2.9-1.1c0-.09.08-.17.17-.17h2.73c.09 0 .17.08.17.17 0 .09-.08.17-.17.17H7.21c-.09 0-.17-.08-.17-.17Zm2.9-.75H7.21c-.09 0-.17-.08-.17-.17 0-.09.08-.17.17-.17h2.73c.09 0 .17.08.17.17 0 .09-.08.17-.17.17Zm1.65-.33c-.07.3-.23.77-.23.77V6.48l.35-.19s-.07.61-.12.8Zm.14-1.53-.73.46H6.19l-.79-.49s-.01-.62.08-1.27c.04-.3 1.23-.78 3.09-.78 1.86 0 3.04.52 3.1.76.17.65.07 1.32.07 1.32h-.01ZM22.09 9.84h-4.32V20h4.32V9.84ZM21.69 8.02h-3.5c-.23 0-.41.16-.41.36v1.14h4.32V8.38c0-.2-.18-.36-.41-.36ZM18.31 7.77h3.33l-.08-7.4c.04-.24-.28-.35-.49-.2-.79.55-2.77 2.18-2.77 4.76 0 1.64.01 2.85.01 2.85v-.01Z" />
            </g>
            <defs>
              <clipPath id="a">
                <path fill="#fff" d="M0 0h22.1v20H0z" />
              </clipPath>
            </defs>
          </svg>
        </div>
        {/* <div className={`ml-1 text-truncate fx fx__v-center ${css(styles.patientCardField)}`} >
        </div> */}
        <div className={`ml-2 text-truncate ${css(styles.patientCardFieldValue)}`}>
          {surgeonString}
        </div>
      </div>
      {/* Surgery/Procedures */}
      <div className='fx' style={{ height: '24px' }}>
        <div className='fx fx__hv-center' style={{ width: '22px', height: '22px' }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none"><path fill="#00A0FF" d="M18.353 4.467a.292.292 0 0 0-.49.317A9.742 9.742 0 0 1 19.417 10a9.809 9.809 0 0 1-.766 3.775.289.289 0 0 0 .15.38l.005.003a.288.288 0 0 0 .38-.15C19.709 12.783 20 11.373 20 10c0-1.956-.607-3.93-1.647-5.533ZM13.169.507c-.978-.32-2.053-.5-3.112-.507A10.012 10.012 0 0 0 2.989 2.87 9.937 9.937 0 0 0 0 9.999c0 1.369.319 2.784.839 4.005a.29.29 0 0 0 .404.143.29.29 0 0 0 .133-.371C.886 12.626.583 11.288.583 10c0-2.515.979-4.879 2.757-6.657A9.406 9.406 0 0 1 10 .583c1.016 0 2.048.17 2.987.478a.292.292 0 0 0 .182-.554ZM17.146 2.993a.259.259 0 0 0-.357.085l-.798 1.298-1.979-1.225.93-1.494a.26.26 0 0 0-.44-.274l-1.067 1.715-.778 1.25-1.52 2.445a.328.328 0 0 0 .101.446l.216.132-5.77 9.202a.338.338 0 0 0 .013.374.314.314 0 0 0 .328.125c.07-.017.183-.037.326-.062.794-.14 2.446-.43 3.85-1.405 1.742-1.208 2.52-3.047 2.313-5.465l.94-1.542.119.073a.33.33 0 0 0 .246.042.327.327 0 0 0 .205-.148l1.482-2.41.76-1.235.061-.1.904-1.47a.26.26 0 0 0-.085-.357Zm-5.163 7.024.008.086c.214 2.26-.487 3.968-2.085 5.077-1.26.875-2.766 1.165-3.554 1.304l5.521-8.806 1.135.655-1.025 1.684Zm1.702-1.89-.002.004-.16-.098-.003-.002-1.84-1.13 1.278-2.056L14.932 6.1l-1.247 2.027Zm1.518-2.47-1.971-1.252.507-.814 1.98 1.227-.516.84Z" /><path fill="#000" d="m13.523 8.032.16.098.002-.004-.162-.094ZM16.327 4.82a.259.259 0 0 1-.062.1l.062-.1Z" /><path fill="#00A0FF" d="M18.309 15.654c-.338.488-.802.98-1.23 1.408A10.007 10.007 0 0 1 10 20a9.961 9.961 0 0 1-7.07-2.929 11.985 11.985 0 0 1-1.19-1.434.291.291 0 1 1 .477-.336c.307.434.741.974 1.125 1.358a9.355 9.355 0 0 0 6.655 2.758 9.396 9.396 0 0 0 6.509-2.607 9.56 9.56 0 0 0 1.089-1.238c-2.221-1.08-4.734-1.43-6.485-1.532-1.995-.116-3.33.042-3.344.044a.26.26 0 0 1-.062-.515c.06-.007 1.386-.164 3.425-.047 1.886.109 4.714.468 7.092 1.714a.26.26 0 0 1 .14.23.286.286 0 0 1-.051.188Z" /><path fill="#00A0FF" d="M3.352 15.182a.3.3 0 1 0 0-.6.3.3 0 0 0 0 .6ZM4.783 14.735a.3.3 0 1 0 0-.6.3.3 0 0 0 0 .6ZM6.245 14.412a.3.3 0 1 0 0-.6.3.3 0 0 0 0 .6Z" /></svg>
        </div>
        {/* <div className={`ml-1 text-truncate fx fx__v-center ${css(styles.patientCardField)}`}></div> */}
        <div className={`ml-2 text-truncate ${css(styles.patientCardFieldValue)}`}>
          {procString}
        </div>
      </div>
      {/* Anesthesia */}
      <div className='fx' style={{ height: '24px' }}>
        <div className='fx fx__hv-center' style={{ width: '22px', height: '22px' }}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="19"
            height="20"
            fill="none"
          >
            <g fill="#00A0FF" clipPath="url(#a)">
              <path d="M.335 12.642a.334.334 0 0 1-.28-.519l.01-.013 1.807-2.427-1.534.027a.263.263 0 0 1-.01-.527l1.926-.034a.337.337 0 0 1 .286.519l-.01.013-1.804 2.424 1.683-.045a.263.263 0 1 1 .014.527l-2.078.055h-.01ZM3.156 15.965a.317.317 0 0 1-.266-.493l.01-.012 1.303-1.752-1.044.019h-.005a.264.264 0 0 1-.004-.527l1.47-.026a.32.32 0 0 1 .271.492l-.01.013-1.3 1.748 1.157-.031a.264.264 0 0 1 .014.527l-1.587.042h-.008ZM5.442 18.785a.304.304 0 0 1-.255-.472l.008-.013.907-1.217-.657.012a.263.263 0 0 1-.01-.527l1.11-.02a.305.305 0 0 1 .26.472l-.01.014-.902 1.212.74-.02a.263.263 0 1 1 .014.527l-1.197.032H5.44ZM12.204 4.038l-.706-.34a.264.264 0 0 0-.228.475l.602.29-1.026 2.096a.268.268 0 0 0-.027.045c-.007.014-.01.027-.015.04l-1.938 3.964a.263.263 0 0 0 .473.231l1.843-3.77 3.728 1.778-.295.591L13.7 9.1a.263.263 0 0 0-.183.494l.862.318-.334.67-.853-.34a.263.263 0 1 0-.194.49l.812.323-.334.67-.84-.392a.263.263 0 0 0-.223.477l.827.387-.316.635-.868-.363a.263.263 0 1 0-.203.487l.835.349-.204.41-.113.226-.888-.36a.264.264 0 0 0-.198.489l.85.344-.378.756c-.055.098-.092.127-.322.195l-1.363.319-.03.009c-.053.005-.205-.066-.288-.104l-.09-.04-.109-.057a1.304 1.304 0 0 0-.311-.16l-.367-.19-.015-.006c-.165-.073-.251-.153-.305-.285l-.415-1.143-.003-.008c-.081-.202-.083-.244-.017-.374l.689-1.359a.264.264 0 0 0-.47-.238l-.689 1.358c-.168.331-.105.552-.004.804l.416 1.144.003.008c.057.144.139.262.248.362-.285.18-.469.509-.469.904 0 .316.178.609.445.8L7.06 19.622a.263.263 0 0 0 .473.232l1.242-2.536c.069.013.14.02.211.02.587 0 1.063-.459 1.063-1.022a.746.746 0 0 0-.006-.095c.055 0 .11-.008.165-.027l1.355-.317.015-.004c.275-.081.482-.16.643-.452l.735-1.47.312-.626a.271.271 0 0 0 .03-.051l.005-.02 1.081-2.168a.26.26 0 0 0 .042-.07c.004-.01.006-.02.008-.03l2.128-4.268.495.237a.264.264 0 0 0 .352-.122.264.264 0 0 0-.124-.352l-1.961-.943 1.827-3.404 1.182.591a.264.264 0 0 0 .236-.471L14.116.028A.264.264 0 0 0 13.88.5l1.286.643-1.779 3.465-.913-.439M8.984 16.81a.59.59 0 0 1-.117-.01.258.258 0 0 0-.253-.104c-.144-.095-.243-.239-.243-.387 0-.252.142-.523.453-.523.079 0 .16.017.239.046l.192.099c.154.104.266.249.266.384 0 .273-.241.495-.537.495Zm3.361-12.118.801.385-.967 1.885-.766-.365.932-1.905Zm2.799 3.684-.897-.428.556-1.112a.264.264 0 0 0-.47-.236l-.561 1.121-1.117-.533.966-1.883 2.464 1.184-.941 1.886v.001Zm-.299-3.067-.984-.473 1.775-3.457 1.04.52-1.831 3.411Z" />
            </g>
            <defs>
              <clipPath id="a">
                <path fill="#fff" d="M0 0h18.712v20H0z" />
              </clipPath>
            </defs>
          </svg>
        </div>
        {/* <div className={`ml-1 text-truncate fx fx__v-center ${css(styles.patientCardField)}`}></div> */}
        <div className={`ml-2 text-truncate ${css(styles.patientCardFieldValue)}`}>
          {anesthesiaString}
        </div>
      </div>
      {/* Schedule Times */}
      <div className='fx fx__v-center' style={{ height: '24px' }}>
        {/* Start Time */}
        <div className='fx fx__hv-center' style={{ width: '22px', height: '22px' }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none"><g clipPath="url(#a)"><path fill="#00A0FF" fillRule="evenodd" d="M9.99 0C4.47 0 0 4.48 0 10s4.47 10 9.99 10C15.51 20 20 15.52 20 10S15.52 0 9.99 0Zm0 18.01c-4.42 0-8-3.58-8-8s3.59-8 8.01-8 8 3.58 8 8-3.58 8-8 8h-.01ZM8.99 5h1.5v5.25l4.5 2.67-.75 1.23L8.99 11V5Z" clipRule="evenodd" /></g><defs><clipPath id="a"><path fill="#fff" d="M0 0h20v20H0z" /></clipPath></defs></svg>
        </div>
        <div className='ml-2' style={{ color: 'white', fontSize: '12px' }}>
          {scheduleStartHour !== null && scheduleStartMinute !== null
            ? `${padZero(scheduleStartHour)}${padZero(scheduleStartMinute)}`
            : 'N/A'
          }
        </div>
        {/* Stop Time */}
        <div className={`mb-1 ml-1 text-truncate fx fx__v-center ${css(styles.patientCardField)}`}>
          -
        </div>
        <div className='ml-2' style={{ color: 'white', fontSize: '12px' }}>
          {scheduleEndHour !== null && scheduleEndMinute !== null
            ? `${padZero(scheduleEndHour)}${padZero(scheduleEndMinute)}`
            : 'N/A'
          }
        </div>
        {/* Minutes */}
        <div className='ml-2' style={{ color: 'white', fontSize: '12px' }}>
          {props.isConcurrent &&
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="14"
              fill="none"
              style={{ marginTop: '3px' }}
            >
              <path fill="#FF0" d="M11 12.74c1.657 0 3-2.57 3-5.74 0-3.17-1.343-5.74-3-5.74S8 3.83 8 7c0 3.17 1.343 5.74 3 5.74Z" />
              <path fill="#FF0" d="M8.04 11.87c-.33.08-.68.13-1.04.13-2.76 0-5-2.24-5-5s2.24-5 5-5c.36 0 .71.05 1.04.13.39-.56.88-1.12 1.49-1.63C8.75.19 7.9 0 7 0 3.14 0 0 3.14 0 7s3.14 7 7 7c.9 0 1.75-.19 2.53-.5-.61-.51-1.1-1.07-1.49-1.63ZM15 0c-.9 0-1.75.19-2.53.5.61.51 1.1 1.07 1.49 1.63.33-.08.68-.13 1.04-.13 2.76 0 5 2.24 5 5s-2.24 5-5 5c-.36 0-.71-.05-1.04-.13-.39.56-.88 1.12-1.49 1.63.78.31 1.63.5 2.53.5 3.86 0 7-3.14 7-7s-3.14-7-7-7Z" />
            </svg>
          }
          {!props.isConcurrent && `(${blockDuration} mins)`}
        </div>
      </div>
    </div>
  );
};

CaseCard.defaultProps = {
  isConcurrent: false
};
export default CaseCard;
